import { useImperativeHandle, forwardRef } from "react";
import {
  LocationHandlerProps,
  LocationHandlerProvider,
  LocationHandlerRef,
  useLocationHandlerContext,
} from "./providers";
import { Location } from "../../Models/Location.ts";
import SearchLocality, {
  SearchLocalityProps,
} from "../../Components/Selects/SearchLocality/SearchLocality.tsx";
import ButtonConfirm from "../../Components/Buttons/Confirm.tsx";
import ButtonUtility from "../../Components/Buttons/Utility.tsx";
import Input from "../../Components/Inputs/Input/Input.tsx";
import { useAppContext } from "../../AppProvider.jsx";
import { getFieldError } from "../../utilities/utilities.ts";
import { createModalConfig } from "../../Models/ModalConfig.ts";
import {
  faLoader,
  faLocationDot,
  faPenSquare,
} from "../../utilities/fontawesomeIcons.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, ModalBody } from "reactstrap";
import InputBoxIcon from "../../Components/Inputs/InputIcon/InputIcon.tsx";

/* ============================================================================
 * Internal Handler UI Component
 * ========================================================================== */

const _LocationHandler = ({
  name = "location",
  isDisabled,
  error,
  className = "",
}: SearchLocalityProps) => {
  const { dictionary } = useAppContext();

  const {
    modalConfig,
    setModalConfig,
    getLocation,
    data,
    action,
    loader,
    saveLocation,
    error: validationError,
  } = useLocationHandlerContext();

  const handleChange = (
    name: string,
    value: string | number | boolean | object | null,
    key: string
  ) => {
    saveLocation(
      {
        ...data,
        [name]: value,
      } as Location,
      key as keyof Location,
      false
    );
  };

  return (
    <>
      <div className={`row ${className}`}>
        {!isDisabled ? (
          <div className="d-flex w-100">
            <div className="flex-grow-1">
              <SearchLocality
                isDisabled={isDisabled}
                name={name}
                value={data}
                onChange={(_, option) => {
                  saveLocation(
                    {
                      ...data,
                      ...option.place,
                    } as Location,
                    null,
                    true
                  );
                }}
                error={error}
              />
            </div>

            <div className="d-flex align-items-start mb-1 ms-1">
              <ButtonConfirm
                className="button-1-icon input-rel"
                icon={false}
                disabled={!data}
                title={`${
                  dictionary.actions.edit
                } ${dictionary.locations.address.toLowerCase()}`}
                onClick={() =>
                  setModalConfig({
                    isOpen: true,
                    action: "editLocation",
                    size: "md",
                    data: [data?.id],
                    title: `${
                      dictionary.actions.edit
                    } ${dictionary.locations.address.toLowerCase()}`,
                  })
                }
              >
                <FontAwesomeIcon icon={faPenSquare} />
              </ButtonConfirm>
            </div>
          </div>
        ) : (
          <div className="col">
            <InputBoxIcon
              icon={faLocationDot}
              name={name}
              disabled
              value={data?.label ?? ""}
            />
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        <ModalBody>
          <div className="row mb-3">
            <div className="col">
              <label className="label-4 text-center">{modalConfig.title}</label>
            </div>
          </div>

          {loader ? (
            <div className="row">
              <div className="col text-center">
                <FontAwesomeIcon icon={faLoader} spin size="2x" />
              </div>
            </div>
          ) : (
            <>
              {/* Address fields */}
              <div className="row gx-2">
                <div className="col-9 col-sm-10 mb-1">
                  <label className="label-2">
                    {dictionary.locations.address}
                  </label>
                  <Input
                    name="street"
                    value={data?.street ?? ""}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value, "street")
                    }
                    disabled={action === "details"}
                    error={getFieldError(validationError, "street")}
                  />
                </div>

                <div className="col-3 col-sm-2 mb-1">
                  <label className="label-2">
                    {dictionary.locations.house_number}
                  </label>
                  <Input
                    name="houseNumber"
                    value={data?.houseNumber ?? ""}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value, "houseNumber")
                    }
                    disabled={action === "details"}
                    error={getFieldError(validationError, "houseNumber")}
                  />
                </div>
              </div>

              <div className="row gx-2">
                <div className="col-6 col-sm-3 mb-1">
                  <label className="label-2">{dictionary.locations.unit}</label>
                  <Input
                    name="unit"
                    value={data?.unit ?? ""}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value, "unit")
                    }
                    disabled={action === "details"}
                    error={getFieldError(validationError, "unit")}
                  />
                </div>

                <div className="col-6 col-sm-3 mb-1">
                  <label className="label-2">
                    {dictionary.locations.postal_code}
                  </label>
                  <Input
                    name="postalCode"
                    value={data?.postalCode ?? ""}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value, "postalCode")
                    }
                    disabled={action === "details"}
                    error={getFieldError(validationError, "postalCode")}
                  />
                </div>

                <div className="col-6 col-sm-3 mb-1">
                  <label className="label-2">
                    {dictionary.locations.district}
                  </label>
                  <Input
                    name="district"
                    value={data?.district ?? ""}
                    disabled
                    error={getFieldError(validationError, "district")}
                  />
                </div>

                <div className="col-6 col-sm-3 mb-1">
                  <label className="label-2">{dictionary.locations.city}</label>
                  <Input
                    name="municipality"
                    value={data?.municipality ?? ""}
                    disabled
                    error={getFieldError(validationError, "municipality")}
                  />
                </div>
              </div>

              {/* Geographic fields */}
              <div className="row gx-2">
                <div className="col-6 col-sm-4 mb-1">
                  <label className="label-2">
                    {dictionary.locations.province}
                  </label>
                  <Input
                    name="subRegionName"
                    value={data?.subRegionName ?? ""}
                    disabled
                    error={getFieldError(validationError, "subRegionName")}
                  />
                </div>

                <div className="col-6 col-sm-4 mb-1">
                  <label className="label-2">
                    {dictionary.locations.region}
                  </label>
                  <Input
                    name="regionName"
                    value={data?.regionName ?? ""}
                    disabled
                    error={getFieldError(validationError, "regionName")}
                  />
                </div>

                <div className="col-6 col-sm-4 mb-1">
                  <label className="label-2">
                    {dictionary.locations.country}
                  </label>
                  <Input
                    name="countryIsoCode3"
                    value={data?.countryIsoCode3 ?? ""}
                    disabled
                    error={getFieldError(validationError, "countryIsoCode3")}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="row gx-2 mt-2">
                <div className="col-6">
                  <ButtonUtility
                    className="w-100"
                    onClick={() => {
                      setModalConfig(createModalConfig());
                      if (data?.id) getLocation(data.id);
                    }}
                    disabled={loader}
                  >
                    {dictionary.actions.cancel}
                  </ButtonUtility>
                </div>

                <div className="col-6">
                  <ButtonConfirm
                    className="w-100"
                    onClick={() => saveLocation(data as Location, "id", true)}
                    disabled={loader}
                    loading={loader}
                  >
                    {dictionary.actions.save}
                  </ButtonConfirm>
                </div>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

/* ============================================================================
 * Parent-facing wrapper with Provider + Ref exposure
 * ========================================================================== */

const LocationHandlerComponent = forwardRef<
  LocationHandlerRef,
  LocationHandlerProps
>(
  (
    {
      id,
      action,
      name,
      service,
      isDisabled = false,
      autosave = false,
      className,
      error,
      callback,
    },
    ref
  ) => {
    return (
      <LocationHandlerProvider
        id={id}
        callback={callback}
        action={action}
        service={service}
        autosave={autosave}
      >
        <LocationHandlerWithRef
          ref={ref}
          name={name}
          isDisabled={isDisabled}
          error={error}
          className={className}
        />
      </LocationHandlerProvider>
    );
  }
);

/* ============================================================================
 * Inner component exposing the imperative API through useImperativeHandle
 * ========================================================================== */

const LocationHandlerWithRef = forwardRef<
  LocationHandlerRef,
  Omit<
    LocationHandlerProps,
    "id" | "action" | "service" | "autosave" | "callback"
  >
>((props, ref) => {
  const { saveLocation } = useLocationHandlerContext();

  useImperativeHandle(ref, () => ({
    saveLocation,
  }));

  return <_LocationHandler {...props} />;
});

export const LocationHandler = LocationHandlerComponent;
